import { FC, useState } from 'react';

import { CorePublicEventProperties } from '@shared/types/event';
import {
  PublicUserConnection,
  PublicUserConnectionComparison,
  PublicUserConnectionStatus,
  UserConnectionStatus,
} from '@shared/types/userConnection';

import { useEventQuery } from '@queries/EventQueries';
import { useUserConnectionsQuery } from '@queries/UserConnectionsQueries';

import { useInviteConnectionMutation } from '@mutations/UserConnectionMutations';

import { sendSmsMessage } from '@utilities/sendSmsMessage';

import Button from '@components/buttons/Button';
import { EventImage } from '@components/cards/EventCard';
import { MultipleChoiceList } from '@components/form/MultipleChoiceList';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { Popup } from '@components/layout/Popup/Popup';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

interface EventInvitePopupContentProps {
  event: CorePublicEventProperties;
  connections: PublicUserConnection[] | undefined;
  inviteUrl: string | undefined;
  onClose: () => void;
}

interface EventInvitePopupProps extends Omit<
  EventInvitePopupContentProps,
  'connections' | 'inviteUrl' | 'event'
> {
  isVisible: boolean;
  eventId: string;
}

const PopupContent: FC<EventInvitePopupContentProps> = ({
  event,
  inviteUrl,
  connections = [],
  onClose,
}) => {
  const [selectedValue, setSelectedValue] = useState<Record<string, boolean>>({});
  const selectedCount = Object.keys(selectedValue).length;
  const { isInvitingConnection, inviteConnectionAsync } = useInviteConnectionMutation();

  const handleSmsInvite = () => {
    sendSmsMessage({
      message: `I'm going to ${event.title}! Are you? Let me know at the link: ${inviteUrl}`,
    });
  };

  const handleChange = (updatedValue: string[]) => {
    setSelectedValue(Object.fromEntries(updatedValue.map((value) => [value, true])));
  };

  const handleSendInvites = async () => {
    const selectedConnections = connections
      .filter((connection) => connection.status === PublicUserConnectionStatus.Accepted)
      .filter((connection) => selectedValue[connection._id]);

    for (const connection of selectedConnections) {
      await inviteConnectionAsync({ otherUserId: connection.connectionUserId, eventId: event._id });
    }

    onClose();
  };

  return (
    <div>
      <TitleToolbar
        text="Invite Friends"
        size="md"
        action={
          <Button
            className="px-3 py-1"
            size="sm"
            disabled={!selectedCount || isInvitingConnection}
            onClick={handleSendInvites}
          >
            {selectedCount ? `Invite ${selectedCount} friends` : `Send invite`}
          </Button>
        }
      />
      {connections.length > 0 ? (
        <>
          <p className="mt-0">
            Select some of your existing connections to invite to this event or invite through text.
          </p>
          <div className="mb-2">
            <Button roundness="rounded" onClick={handleSmsInvite}>
              Invite throught text
            </Button>
          </div>
          <MultipleChoiceList
            isMultiSelect
            options={connections
              .filter((connection) => connection.status === PublicUserConnectionStatus.Accepted)
              .map((connection) => ({
                label: connection.connectionName,
                value: connection._id,
              }))}
            onChange={handleChange}
          />
        </>
      ) : (
        <EmptyState
          title="No connections yet"
          text="You don't have any connections yet. Use the button below to text your invite to friends."
          button={{
            text: 'Invite by text',
            onClick: () => {
              handleSmsInvite();
            },
          }}
        />
      )}
    </div>
  );
};

export const EventInvitePopup: FC<EventInvitePopupProps> = ({
  isVisible: controlledIsVisible,
  eventId,
  ...props
}) => {
  const { event, userEventInviteUrl } = useEventQuery(eventId);

  const { connections } = useUserConnectionsQuery();
  const isVisible = controlledIsVisible && !!event && !!userEventInviteUrl;

  return (
    <Popup isVisible={isVisible} onClose={props.onClose}>
      <Popup.Content>
        {isVisible && event?._id ? (
          <PopupContent
            connections={connections}
            event={event}
            inviteUrl={userEventInviteUrl}
            {...props}
          />
        ) : (
          <Skeleton height={240} />
        )}
      </Popup.Content>
    </Popup>
  );
};
